import { useState } from "react";
import { Layers, Copy, Trash2, Scissors } from "lucide-react";
import { toast } from "sonner";

const GROUP_SIZE = 30;

export default function Grouper() {
  const [raw, setRaw] = useState("");
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);

  const split = () => {
    const seen = new Set();
    const names = [];
    for (const part of raw.split(/[\s,;]+/)) {
      const n = part.trim().replace(/^@+/, "");
      if (!n) continue;
      const key = n.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(n);
    }
    if (!names.length) {
      toast.error("Önce isim listesi yapıştırın");
      return;
    }
    const out = [];
    for (let i = 0; i < names.length; i += GROUP_SIZE) out.push(names.slice(i, i + GROUP_SIZE));
    setGroups(out);
    setTotal(names.length);
    toast.success(`${names.length} benzersiz isim ${out.length} gruba ayrıldı`);
  };

  const copyGroup = async (i) => {
    try {
      await navigator.clipboard.writeText(groups[i].join("\n"));
      toast.success(`Grup ${i + 1} kopyalandı (${groups[i].length} isim)`);
    } catch {
      toast.error("Panoya kopyalanamadı");
    }
  };

  const clear = () => {
    setRaw("");
    setGroups([]);
    setTotal(0);
  };

  return (
    <div data-testid="grouper-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
          <Layers size={20} className="text-cyan-300" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-white">İsim Gruplama</h1>
          <p className="text-sm text-[#A0ABC0]">İsimleri yapıştırın — 30'arlı gruplara ayrılır, her grup tek tıkla kopyalanır</p>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
        <textarea
          data-testid="grouper-input"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={7}
          placeholder={"İsimleri buraya yapıştırın (alt alta, virgüllü veya boşluklu olabilir)\n@ornek1\nornek2, ornek3 ..."}
          className="w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm text-white font-mono outline-none focus:border-cyan-400/60 transition-colors resize-y"
        />
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="grouper-split-button" onClick={split} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-400/15 border border-cyan-400/30 text-cyan-300 text-sm font-medium hover:bg-cyan-400/25 transition-colors">
            <Scissors size={14} /> 30'lu Gruplara Ayır
          </button>
          <button data-testid="grouper-clear-button" onClick={clear} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-[#A0ABC0] text-sm hover:bg-white/10 transition-colors">
            <Trash2 size={14} /> Temizle
          </button>
          {groups.length > 0 && (
            <span data-testid="grouper-status" className="text-xs text-[#A0ABC0]">
              {total} benzersiz isim → {groups.length} grup
            </span>
          )}
        </div>
      </div>

      {groups.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {groups.map((g, i) => (
            <div key={i} data-testid={`grouper-group-${i + 1}`} className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-white">
                  Grup {i + 1} <span className="text-xs font-mono text-cyan-300">({g.length} isim)</span>
                </span>
                <button data-testid={`grouper-copy-${i + 1}`} onClick={() => copyGroup(i)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-400/15 border border-cyan-400/30 text-cyan-300 text-xs font-medium hover:bg-cyan-400/25 transition-colors">
                  <Copy size={12} /> Kopyala
                </button>
              </div>
              <div className="max-h-56 overflow-y-auto bg-black/30 rounded-xl p-3">
                <pre className="text-xs font-mono text-[#A0ABC0] whitespace-pre-wrap break-all">{g.join("\n")}</pre>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
